/** @typedef {'NotFound'|'Period'|'Percent'|'Count'} ShowType */

export const ShowType = {
  NotFound: 'NotFound',
  Period: 'Period',
  Percent: 'Percent',
  Count: 'Count'
}

/**
 * @param {any} item
 * @returns {string}
 */
export function resolveDeviceSeq(item) {
  if (!item) return ''
  return String(item.deviceSeq || item.deviceMac || item.mac || item.deviceCode || '').trim()
}

/**
 * 转为接口要求的 recordStartTime：yyyy-MM-dd'T'HH:mm:ss
 * @param {any} raw
 * @returns {string}
 */
export function toApiRecordStartTime(raw) {
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(s)) {
    return s.slice(0, 19)
  }
  const m = s.match(/(\d{4})[-/年](\d{1,2})[-/月](\d{1,2})[日\sT]+(\d{1,2}):(\d{2})(?::(\d{2}))?/)
  if (m) {
    const pad = (n) => String(n).padStart(2, '0')
    return `${m[1]}-${pad(m[2])}-${pad(m[3])}T${pad(m[4])}:${pad(m[5])}:${pad(m[6] || '0')}`
  }
  const normalized = s.replace(' ', 'T')
  if (/^\d{4}-\d{2}-\d{2}T/.test(normalized)) {
    return normalized.slice(0, 19)
  }
  const d = new Date(s)
  if (!Number.isNaN(d.getTime())) {
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  }
  return normalized.slice(0, 19)
}

/**
 * @param {number} ms
 * @returns {string}
 */
export function formatDurationMs(ms) {
  if (!ms || ms <= 0) return '0秒'
  const sec = Math.floor(ms / 1000)
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const parts = []
  if (h > 0) parts.push(`${h}小时`)
  if (m > 0) parts.push(`${m}分`)
  if (s > 0 || parts.length === 0) parts.push(`${s}秒`)
  return parts.join('')
}

/**
 * @param {string} durationStr
 * @returns {number}
 */
export function parseDurationStrToSeconds(durationStr) {
  if (!durationStr) return 0
  const s = String(durationStr).trim()
  if (/^\d+$/.test(s)) return Number(s)
  let sec = 0
  const h = s.match(/(\d+)小时/)
  const m = s.match(/(\d+)分/)
  const secM = s.match(/(\d+)秒/)
  if (h) sec += Number(h[1]) * 3600
  if (m) sec += Number(m[1]) * 60
  if (secM) sec += Number(secM[1])
  return sec
}

/**
 * @param {number} frequencyValue
 * @returns {string}
 */
export function getFrequencyLevel(frequencyValue) {
  if (frequencyValue < 0.01) return '正常'
  if (frequencyValue < 0.04) return '偶发'
  return '频发'
}

/**
 * 解析病症展示类型（对齐小程序 record-analysis-logic）
 * @param {any} item
 * @param {number} totalHeartBeats
 * @returns {ShowType}
 */
export function resolveShowType(item, totalHeartBeats) {
  const count = Number(item && item.count) || 0
  if (count <= 0) return ShowType.NotFound
  const name = String((item && item.name) || '')
  const timesPointEventNames = ['按键', '停搏', '传导阻滞']
  if (name.indexOf('房颤') >= 0 || name.indexOf('ST段') >= 0) {
    return ShowType.Period
  }
  if (item.isPoint === true) {
    if (timesPointEventNames.some((x) => name.includes(x))) {
      return ShowType.Count
    }
    return ShowType.Percent
  }
  return ShowType.Count
}

/**
 * @param {any[]} list
 * @returns {number}
 */
export function sumSectionDurationMs(list) {
  if (!Array.isArray(list)) return 0
  return list.reduce((sum, row) => {
    const a = row && row.startTime ? new Date(row.startTime).getTime() : NaN
    const b = row && row.endTime ? new Date(row.endTime).getTime() : NaN
    if (!Number.isNaN(a) && !Number.isNaN(b) && b > a) {
      return sum + (b - a)
    }
    const len = Number(row && row.timeLength) || 0
    return sum + len * 1000
  }, 0)
}

/**
 * @param {any[]} list
 * @returns {number}
 */
export function sumPointDurationMs(list) {
  if (!Array.isArray(list)) return 0
  return list.reduce((sum, row) => {
    const len = Number(row && row.timeLength) || 6
    return sum + len * 1000
  }, 0)
}

/**
 * @param {string} ecgDataStr
 * @returns {{ sampling: number, data: number[] }}
 */
export function parseEcgDataJson(ecgDataStr) {
  try {
    const o = typeof ecgDataStr === 'string' ? JSON.parse(ecgDataStr) : ecgDataStr
    const sampling = Number(o && o.sampling) || 250
    let data = []
    if (o && Array.isArray(o.ecgData)) {
      if (Array.isArray(o.ecgData[0])) data = o.ecgData[0]
      else if (typeof o.ecgData[0] === 'number') data = o.ecgData
    }
    return { sampling, data: data.map((v) => Number(v) || 0) }
  } catch (e) {
    return { sampling: 250, data: [] }
  }
}

/**
 * @param {string} timeStr
 * @returns {string}
 */
export function formatEventTimeText(timeStr) {
  if (!timeStr) return '--'
  const d = new Date(timeStr)
  if (Number.isNaN(d.getTime())) return String(timeStr)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}



// WEBPACK FOOTER //
// ./src/utils/ecgAnalysisHelper.js